import { z } from 'zod';
import { folderKindFor } from '../../domain/entities/file.ts';
import { RecordRefSchema } from '../../domain/entities/notification.ts';
import { isAttachableKind } from './record-attachments.ts';
import { newId } from '../../domain/id.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import type { StoragePort } from '../../ports/storage-port.ts';
import { checkUploadable, FileUploadError, storagePathFor } from './upload-file.ts';

/**
 * An owner or a subcontractor uploading a file (P3-04b2).
 *
 * The same two stores and the same order as `uploadFile` — bytes first,
 * then the record — for the same reason: an object with no document is
 * invisible and costs storage, while a document with no object is a file
 * that appears in every list, cannot be opened, and has to be explained
 * to whoever uploaded it.
 *
 * **What differs is who writes the second one.** An external member
 * holds `files: view`, so the document is created by
 * `recordExternalUpload` rather than by this client. That is not a
 * detour around the rules, it is the rules: the folder, the size, the
 * content type and the audience are all decided from *who is asking*,
 * and a client that could state them could file anything anywhere, at
 * any claimed size, shared with anybody.
 *
 * So this function sends bytes and asks. It computes no audience and
 * names no folder, because it is not entitled to either answer.
 */
export const ExternalUploadInputSchema = z.object({
  jobId: z.string().min(1),
  name: z.string().trim().min(1).max(260),
  contentType: z.string().min(1),
  size: z.number().int().nonnegative(),
  /**
   * The record to attach it to, if any (P8-12a2).
   *
   * Passed straight through. This side does **not** check whether the
   * caller may read it — it could only mirror an answer it does not
   * have, and a mirror that disagreed with the server would be worse
   * than no mirror at all.
   */
  record: RecordRefSchema.optional(),
});
export type ExternalUploadInput = z.infer<typeof ExternalUploadInputSchema>;

export interface ExternalUploadContext {
  readonly storage: StoragePort;
  readonly functions: FunctionsPort;
  readonly companyId: string;
}

export interface ExternalUploadResult {
  readonly fileId: string;
  readonly folderId: string | null;
  /** True when this upload had already been recorded — see below. */
  readonly duplicate: boolean;
}

export async function uploadAsExternalMember(
  context: ExternalUploadContext,
  input: ExternalUploadInput,
  data: Blob | Uint8Array,
  onProgress?: (fraction: number) => void,
): Promise<ExternalUploadResult> {
  const parsed = ExternalUploadInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new FileUploadError('noName', parsed.error.issues[0]!.message);
  }
  /**
   * Checked here **as well as** on the server, and this is not a
   * duplicate of that check.
   *
   * Refusing afterwards still spends the bandwidth and the storage
   * write, and on a phone on site that is somebody's data allowance. The
   * server check is the one that decides; this one is the one that is
   * kind. Both have to exist, because a client check alone decides
   * nothing.
   */
  checkUploadable(parsed.data);

  /**
   * The id comes from the client, which is what makes a retry
   * recognisable as the same upload rather than a second file. The
   * function is idempotent on it: a dropped response and a
   * double-tapped button are the same act.
   */
  const fileId = newId();
  const path = storagePathFor(
    context.companyId,
    fileId,
    parsed.data.contentType,
    parsed.data.jobId,
  );

  await context.storage.upload(
    path,
    data,
    {
      contentType: parsed.data.contentType,
      fileName: parsed.data.name,
      size: parsed.data.size,
    },
    onProgress,
  );

  const record = parsed.data.record;
  const recorded = await context.functions.call('recordExternalUpload', {
    companyId: context.companyId,
    jobId: parsed.data.jobId,
    fileId,
    name: parsed.data.name,
    contentType: parsed.data.contentType,
    // Both or neither: the server refuses half a reference rather than
    // reading it as "no record" and dropping the attachment.
    ...(record && isAttachableKind(record.kind)
      ? { recordKind: record.kind, recordId: record.id }
      : {}),
  });

  return {
    fileId: recorded.fileId,
    folderId: recorded.folderId,
    duplicate: recorded.duplicate,
  };
}

/** Where the bytes of an external upload go, for a test or a screen to check. */
export function externalUploadPath(companyId: string, jobId: string, fileId: string, type: string) {
  return { companyId, jobId, folder: folderKindFor(type), fileId };
}
